// Pure helpers for AssignToRoute's mapping mutation shape and cache
// reconciliation — kept here so the logic can be unit-tested without
// rendering the React component.

import { v4 as uuid } from 'uuid';
import { groupBy, mapValues, omit, sortBy } from 'lodash';
import { ROUTE_READONLY_FIELDS } from '@onward-delivery/core';
import { TEMP_ROUTE, TEMP_STOP } from '../constants';
import { emptyUpdateStopsRoots } from '../graphql/updateStopsOptimistic';

export const LEG_ROUTE_TYPES = {
    pickup: 'PICKUP',
    dropoff: 'DROPOFF',
    default: 'FULL',
};

/**
 * Resolve the mapping type for an order based on its crossdock_leg.
 *
 *   crossdock_leg='pickup'  -> 'PICKUP'   (crossdock first leg, origin -> warehouse)
 *   crossdock_leg='dropoff' -> 'DROPOFF'  (crossdock second leg, warehouse -> customer)
 *   anything else           -> 'FULL'    (E2E route, origin -> customer)
 */
export const mappingTypeForOrder = (order) => {
    return LEG_ROUTE_TYPES[order?.crossdock_leg] || LEG_ROUTE_TYPES.default;
};

/**
 * Whether this leg is the one facing the customer — the leg that owns the
 * order's confirmation and del_window_* scalars. A return's customer stop is
 * its PICKUP (the driver collects from the customer) and its warehouse stop is
 * the DROPOFF; every other order type is the other way round. E2E orders (no
 * leg) always face the customer. Same store/customer rule OrderListItem uses
 * to pick the address it renders.
 */
export const isCustomerLeg = (order) =>
    order?.order_type === 'return' ? order?.crossdock_leg !== 'dropoff' : order?.crossdock_leg !== 'pickup';

// The planning query orders routes by route_number asc; cache patches that
// append routes must keep the cached list in that order. Optimistic shell
// routes sort last until the server assigns their real route_number.
export const routeNumberOrder = (route) =>
    Number.isFinite(route.route_number) ? route.route_number : Number.MAX_SAFE_INTEGER;

/**
 * Pure function. Given a batch of orders being assigned to a destination
 * route, compute the `mapping_ids_delete` and `mappings_insert` arrays for the
 * ASSIGN_ORDERS_UPSERT mutation.
 *
 * For each order:
 *   1. Resolve the mapping type from the order's crossdock_leg.
 *   2. If the order was previously assigned to another route on that leg
 *      (pickup leg uses pickup_route_id, dropoff leg / FULL uses
 *      dropoff_route_id), look up the active DISCARD mapping on that previous
 *      route and queue it for deletion. A partial unique index on
 *      (order_id, type) WHERE duplicate_override='DISCARD' guarantees at
 *      most one active mapping per (order_id, type), so this lookup is
 *      unambiguous.
 *   3. Always queue a new DISCARD mapping insert targeting the destination
 *      route.
 *
 * Returns `{ mappingIdsToDelete, mappingsToDelete, mappingsToInsert }` where
 * `mappingsToDelete` holds the full cached mapping objects (needed to echo the
 * mutation's delete returning in an optimistic response). Pure — no side effects.
 *
 * @param {object} args
 * @param {Array<object>} args.ordersToAdd - orders being assigned
 * @param {string} args.assignedRoute - destination route_id
 * @param {object} args.routesByKey - map of route_id -> route (with orders)
 * @returns {{ mappingIdsToDelete: string[], mappingsToDelete: object[], mappingsToInsert: object[] }}
 */
export const buildAssignMutations = ({ ordersToAdd, assignedRoute, routesByKey }) => {
    const mappingsToDelete = [];
    const mappingsToInsert = [];

    for (const order of ordersToAdd) {
        const mappingType = mappingTypeForOrder(order);

        const prevRouteKey = order.crossdock_leg === 'pickup' ? order.pickup_route_id : order.dropoff_route_id;
        if (prevRouteKey) {
            const prevRoute = routesByKey?.[prevRouteKey];
            const prevMapping = (prevRoute?.orders || []).find(
                (m) => m.order_id === order.order_id && m.type === mappingType && m.duplicate_override === 'DISCARD'
            );
            if (prevMapping?.mapping_id) {
                mappingsToDelete.push(prevMapping);
            }
        }

        mappingsToInsert.push({
            order_id: order.order_id,
            route_id: assignedRoute,
            type: mappingType,
            duplicate_override: 'DISCARD',
        });
    }

    return {
        mappingIdsToDelete: mappingsToDelete.map((mapping) => mapping.mapping_id),
        mappingsToDelete,
        mappingsToInsert,
    };
};

/**
 * Pure function. Build ASSIGN_ORDERS_UPSERT's `$routes` objects — one per route
 * the assign touches: the destination gaining stops plus every source route
 * losing them.
 *
 * `routeUpdate` holds the columns the document's
 * `on_conflict: { update_columns: [...] }` is allowed to write, and it is
 * applied LAST, over the cached route. That order is the point: spreading the
 * cached route alone writes those columns back over themselves, so an
 * already-optimized route stays need_to_optimize: false even though its stop
 * set — and therefore every downstream del_window_* — just changed.
 *
 * Shell routes additionally carry the client's staging-verification preference;
 * a real route's stored value is left alone.
 *
 * @param {object} args
 * @param {string[]} args.affectedRouteIds - route_ids the assign writes stops for
 * @param {object} args.routesByKey - map of route_id -> full cached route
 * @param {object} [args.routeUpdate] - on_conflict columns to force (defaults to need_to_optimize: true)
 * @param {boolean} [args.requireStagingVerification] - preferences_require_staging_verification, applied to shells only
 * @returns {Array<object>} routes_insert_input objects
 */
export const buildRouteInserts = ({
    affectedRouteIds,
    routesByKey,
    routeUpdate = { need_to_optimize: true },
    requireStagingVerification,
}) =>
    (affectedRouteIds || []).flatMap((routeId) => {
        const route = routesByKey?.[routeId];
        if (!route) return [];

        const routeInsert = { ...omit(route, ROUTE_READONLY_FIELDS), ...routeUpdate };

        return [route.shell ? { ...routeInsert, needs_verification: requireStagingVerification } : routeInsert];
    });

/**
 * Pure function. Rewrite the cached QUERY_PLANNING_DATA result to reflect an
 * ASSIGN_ORDERS_UPSERT response (optimistic or real) so the board paints
 * without a refetch.
 *
 * The mutation's semantics are full-replace per affected route: delete_stops
 * wipes every stop on $route_ids and insert_stops re-inserts the recomputed
 * sets, so each affected route's stopsByRouteId becomes exactly its slice of
 * insert_stops.returning. Assignment membership moves with the mappings:
 * deleted mapping_ids leave their route's `orders`, inserted mappings join the
 * destination route's `orders` (with the full order row nested, since the
 * query selects the order under each mapping), and each assigned order leaves
 * the unassigned pickups/dropoffs bucket for its leg — mirroring the query's
 * server-side exclusion of orders with an active DISCARD mapping.
 *
 * Routes returned by insert_routes but absent from the cached list (a shell
 * resource route inserted by this assign) are appended, since the board only
 * renders routes present in this query result.
 *
 * @param {object|null} prev - cached QUERY_PLANNING_DATA result
 * @param {object} args
 * @param {object} args.data - ASSIGN_ORDERS_UPSERT response data
 * @param {Array<object>} args.ordersToAdd - full order rows being assigned
 * @returns {object|null} next query result (or `prev` untouched when unpatchable)
 */
export const patchPlanningData = (prev, { data, ordersToAdd, orderUpdates }) => {
    if (!prev?.routes) {
        return prev;
    }

    const returnedRoutes = data?.insert_routes?.returning || [];
    const insertedStops = data?.insert_stops?.returning || [];
    const insertedMappings = data?.insert_order_route_mappings?.returning || [];
    const deletedMappingIds = new Set((data?.delete_order_route_mappings?.returning || []).map((m) => m.mapping_id));

    // Seed freshOrdersById from the client-known _set, then overlay
    // update_orders_many.returning on top so the SERVER's values win. The
    // returning is OrderDispatchScalars — every column any _set here writes —
    // so on the real pass the overlay fully supersedes the seed; the seed only
    // carries the optimistic pass, whose returning is the client's own echo.
    const freshOrdersById = Object.fromEntries(
        (orderUpdates || []).filter((u) => u?.where?.order_id?._eq).map((u) => [u.where.order_id._eq, u._set || {}])
    );
    for (const res of data?.update_orders_many || []) {
        for (const o of res?.returning || []) {
            if (o?.order_id) {
                freshOrdersById[o.order_id] = { ...(freshOrdersById[o.order_id] || {}), ...o };
            }
        }
    }

    const rowsById = Object.fromEntries((ordersToAdd || []).map((o) => [o.order_id, o]));

    const stopsForRoute = mapValues(groupBy(insertedStops, 'route_id'), (stops) => sortBy(stops, 'ordering'));
    const insertedMappingsByRoute = groupBy(insertedMappings, 'route_id');

    // The nested order under a mapping must satisfy the query's full order
    // selection: start from the order row (fetched through that selection),
    // overlay the fresher update_orders_many scalars, and keep the order's own
    // slim `routes` mapping list in sync with the mapping move.
    const orderForMapping = (mapping) => {
        const base = rowsById[mapping.order_id];
        if (!base) {
            return null;
        }
        const fresh = freshOrdersById[mapping.order_id] || {};
        // __typename is required for Apollo to normalize this mapping into a
        // ref in the order's `routes` list. Without it the entry is stored
        // inline, and the unassign patch's removeRefs (which matches by __ref)
        // can't strip it — leaving a stale blocking DISCARD mapping that makes
        // the freed order read INELIGIBLE and silently vanish until a refetch.
        const slimMapping = {
            __typename: 'order_route_mappings',
            mapping_id: mapping.mapping_id,
            order_id: mapping.order_id,
            route_id: mapping.route_id,
            type: mapping.type,
        };
        return {
            ...base,
            ...fresh,
            routes: [...(base.routes || []).filter((m) => !deletedMappingIds.has(m.mapping_id)), slimMapping],
        };
    };

    const mappingsJoiningRoute = (route_id) =>
        (insertedMappingsByRoute[route_id] || [])
            .map((m) => ({ ...m, order: orderForMapping(m) }))
            .filter((m) => m.order);

    // Deduplicate by order_id + type before appending: when multiple in-flight
    // assigns overlap (rapid map-pin clicks), each mutation's optimistic pass
    // adds a temp-uuid mapping and its real-response pass adds the server uuid.
    // Without dedup, both accumulate in route.orders — temp entries linger as
    // phantom entities and earlier real entries get dropped from the list when
    // a later mutation's optimistic layer dominates the prev read. Removing any
    // existing mapping for the same (order_id, type) before adding the new one
    // keeps exactly one entry per order leg throughout the optimistic/real cycle.
    const patchMappings = (mappings, route_id) => {
        const joining = mappingsJoiningRoute(route_id);
        const replacedKeys = new Set(joining.map((m) => `${m.order_id}_${m.type}`));
        return [
            ...(mappings || []).filter(
                (m) => !deletedMappingIds.has(m.mapping_id) && !replacedKeys.has(`${m.order_id}_${m.type}`)
            ),
            ...joining,
        ];
    };

    const returnedById = Object.fromEntries(returnedRoutes.map((r) => [r.route_id, r]));
    const cachedRouteIds = new Set(prev.routes.map((r) => r.route_id));

    const patchedRoutes = prev.routes.map((route) => {
        if (!returnedById[route.route_id]) {
            return route;
        }
        return {
            ...route,
            ...returnedById[route.route_id],
            stopsByRouteId: stopsForRoute[route.route_id] || [],
            orders: patchMappings(route.orders, route.route_id),
        };
    });

    // TEMP_ROUTE-filled for the same reason as the optimistic response (see
    // buildOptimisticAssignResponse): insert_routes.returning only selects
    // RouteFields, missing RouteStagingVerificationFields (staging_verification)
    // and teammateByDriverId that QUERY_PLANNING_DATA also needs on `routes`.
    // Without this, a brand-new (shell -> real) route writes cache-incomplete,
    // the watched planning query can never read a complete result, and the
    // route silently stays invisible until a hard refetch.
    const newRoutes = returnedRoutes
        .filter((r) => !cachedRouteIds.has(r.route_id))
        .map((r) => ({
            ...TEMP_ROUTE,
            teammateByDriverId: null,
            ...r,
            stopsByRouteId: stopsForRoute[r.route_id] || [],
            orders: mappingsJoiningRoute(r.route_id),
        }));

    // Mirrors the query's bucket exclusions: pickups excludes orders with an
    // active mapping of type [PICKUP, FULL], dropoffs excludes [DROPOFF, FULL]
    // — so a FULL assignment removes the order from both buckets.
    const assignedPickupIds = new Set();
    const assignedDropoffIds = new Set();
    for (const mapping of insertedMappings) {
        if (mapping.type === 'PICKUP' || mapping.type === 'FULL') {
            assignedPickupIds.add(mapping.order_id);
        }
        if (mapping.type === 'DROPOFF' || mapping.type === 'FULL') {
            assignedDropoffIds.add(mapping.order_id);
        }
    }

    return {
        ...prev,
        routes: sortBy([...patchedRoutes, ...newRoutes], routeNumberOrder),
        pickups: (prev.pickups || []).filter((o) => !assignedPickupIds.has(o.order_id)),
        dropoffs: (prev.dropoffs || []).filter((o) => !assignedDropoffIds.has(o.order_id)),
    };
};

/**
 * Build the optimistic ASSIGN_ORDERS_UPSERT response so the board paints
 * before the server round trip. Shapes mirror the mutation document exactly;
 * every entity must be fragment-complete or the planning query read goes
 * partial and the board blanks:
 *   - stops: client-recomputed sets, null-filled via TEMP_STOP; stops the
 *     server will create get a temporary uuid (replaced when the real
 *     response lands and the optimistic layer is dropped)
 *   - routes: cached route objects, null-filled via TEMP_ROUTE and overlaid
 *     with `routeUpdate` (the mutation's on_conflict _set, i.e.
 *     need_to_optimize) so the optimistic paint carries the same flag the
 *     server will return — otherwise the "Route modified, map and resubmit"
 *     chip lands a round trip late. Generated shell resource routes lack
 *     route_number (context.js strips it), and a RouteFields-incomplete write
 *     leaves the watched planning query partial, silently defeating the
 *     optimistic paint until the server responds
 *   - orders: the order rows with each order_updates _set applied
 *   - mappings: inserts get a temporary mapping_id; deletes echo the cached
 *     mapping objects
 *
 * @param {object} args
 * @param {Array<object>} args.affectedRoutes - full cached route objects (previous + destination)
 * @param {object} args.stopsByRoute - route_id -> recomputed client stop set
 * @param {Array<object>} args.orderUpdates - the mutation's $order_updates ({ where, _set } entries)
 * @param {Array<object>} args.ordersToAdd - full order rows being assigned
 * @param {Array<object>} args.mappingsToInsert - mapping insert inputs
 * @param {Array<object>} args.mappingsToDelete - full cached mapping objects being deleted
 * @param {object} [args.routeUpdate] - route columns the upsert's on_conflict writes (defaults to need_to_optimize: true)
 * @returns {object} optimisticResponse for ASSIGN_ORDERS_UPSERT
 */
export const buildOptimisticAssignResponse = ({
    affectedRoutes,
    stopsByRoute,
    orderUpdates,
    ordersToAdd,
    mappingsToInsert,
    mappingsToDelete,
    routeUpdate = { need_to_optimize: true },
}) => {
    const setByOrderId = Object.fromEntries(
        orderUpdates.map((update) => [update?.where?.order_id?._eq, update?._set || {}])
    );

    const optimisticStops = Object.entries(stopsByRoute).flatMap(([route_id, stops]) =>
        stops.map((stop) => ({
            ...TEMP_STOP,
            stop_id: stop.stop_id || uuid(),
            ...stop,
            route_id,
            __typename: 'stops',
        }))
    );

    return {
        delete_stops: {
            __typename: 'stops_mutation_response',
            affected_rows: 0,
        },
        delete_order_route_mappings: {
            __typename: 'order_route_mappings_mutation_response',
            affected_rows: mappingsToDelete.length,
            returning: mappingsToDelete.map((mapping) => ({
                __typename: 'order_route_mappings',
                ...mapping,
            })),
        },
        insert_routes: {
            __typename: 'routes_mutation_response',
            returning: affectedRoutes.map((route) => ({
                ...TEMP_ROUTE,
                ...route,
                ...routeUpdate,
                __typename: 'routes',
            })),
        },
        insert_stops: {
            __typename: 'stops_mutation_response',
            affected_rows: optimisticStops.length,
            returning: optimisticStops,
        },
        update_orders_many: [
            {
                __typename: 'orders_mutation_response',
                affected_rows: ordersToAdd.length,
                returning: ordersToAdd.map((order) => ({
                    ...order,
                    ...(setByOrderId[order.order_id] || {}),
                    __typename: 'orders',
                })),
            },
        ],
        insert_order_wh_events: {
            __typename: 'order_wh_events_mutation_response',
            affected_rows: 0,
            returning: [],
        },
        insert_order_route_mappings: {
            __typename: 'order_route_mappings_mutation_response',
            affected_rows: mappingsToInsert.length,
            returning: mappingsToInsert.map((mapping) => ({
                __typename: 'order_route_mappings',
                mapping_id: uuid(),
                created_at: new Date().toISOString(),
                ...mapping,
            })),
        },
    };
};

/**
 * Build the optimistic UNASSIGN_ORDERS_UPDATE response so the board paints a
 * route removal before the server round trip — the inverse of
 * buildOptimisticAssignResponse. The freed order leaves the route (its stop +
 * mapping gone), and patchPlanningDataOnUnassign — running on this same
 * optimistic pass — returns it to its unassigned bucket. Every entity must be
 * fragment-complete or the watched planning query read goes partial and the
 * board blanks, so stops are TEMP_STOP-filled and the route TEMP_ROUTE-filled.
 *
 *   - update_routes_by_pk: the route with its RECOMPUTED post-removal stop set
 *     (`newStops`) and its mapping list minus the deleted mappings. Resolves
 *     last in the real document, so its returning is authoritative there; here
 *     it carries the same post-removal snapshot.
 *   - delete_stops.returning: the dropped stops — patchPlanningDataOnUnassign
 *     evicts them so the argument-less stopsByRouteId keys other pages read
 *     self-heal.
 *   - delete_order_route_mappings.returning: the deleted DISCARD mappings — the
 *     patch derives bucket re-entry candidates from them and strips their refs
 *     from each freed order's slim `routes` list.
 *   - update_orders_many: each freed order with its `_set` overlaid, matching
 *     UNASSIGN_ORDERS_UPDATE's OrderDispatchScalars returning. This is the ONLY
 *     place the client-requested `_set` reaches the cache: it paints the freed
 *     order's post-removal scalars for the optimistic re-bucket, and the real
 *     response's returning overwrites it on landing. `freedOrders` rows come
 *     from route.orders[].order (full OrderFields), so they satisfy the narrow
 *     selection; the overlay is what makes the row post-removal rather than
 *     pre-removal.
 *
 * @param {object} args
 * @param {object} args.route - full cached route object being removed from
 * @param {Array<object>} args.newStops - recomputed post-removal stop set for the route
 * @param {Array<object>} args.deletedStops - full cached stop objects being deleted
 * @param {Array<object>} args.deletedMappings - full cached mapping objects being deleted
 * @param {Array<object>} args.freedOrders - full cached order rows being removed from the route
 * @param {Array<object>} [args.orderUpdates] - the mutation's $order_updates ({ where, _set }), overlaid
 *     onto the matching freedOrders row for the optimistic paint only
 * @param {object} args.routeUpdate - the mutation's $route_update (_set) fields
 * @returns {object} optimisticResponse for UNASSIGN_ORDERS_UPDATE
 */
export const buildOptimisticUnassignResponse = ({
    route,
    newStops,
    deletedStops,
    deletedMappings,
    freedOrders,
    orderUpdates = [],
    routeUpdate,
}) => {
    const deletedMappingIds = new Set((deletedMappings || []).map((mapping) => mapping.mapping_id));
    const setByOrderId = Object.fromEntries(
        orderUpdates.map((update) => [update?.where?.order_id?._eq, update?._set || {}])
    );

    const optimisticStops = (newStops || []).map(({ __typename, ...stop }) => ({
        ...TEMP_STOP,
        stop_id: stop.stop_id || uuid(),
        ...stop,
        route_id: route.route_id,
        __typename: 'stops',
    }));

    return {
        ...emptyUpdateStopsRoots(),
        delete_order_route_mappings: {
            __typename: 'order_route_mappings_mutation_response',
            affected_rows: (deletedMappings || []).length,
            returning: (deletedMappings || []).map((mapping) => ({ __typename: 'order_route_mappings', ...mapping })),
        },
        delete_stops: {
            __typename: 'stops_mutation_response',
            affected_rows: (deletedStops || []).length,
            returning: (deletedStops || []).map(({ __typename, ...stop }) => ({
                ...TEMP_STOP,
                ...stop,
                __typename: 'stops',
            })),
        },
        update_orders_many: [
            {
                __typename: 'orders_mutation_response',
                affected_rows: (freedOrders || []).length,
                returning: (freedOrders || []).map((order) => ({
                    ...order,
                    ...(setByOrderId[order.order_id] || {}),
                    __typename: 'orders',
                })),
            },
        ],
        update_routes_by_pk: {
            ...TEMP_ROUTE,
            ...route,
            ...routeUpdate,
            stopsByRouteId: optimisticStops,
            orders: (route.orders || []).filter((mapping) => !deletedMappingIds.has(mapping.mapping_id)),
            __typename: 'routes',
        },
    };
};
